import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Icon, IconButton, InputAdornment, Paper, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';

import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import { ListParams } from '../../models/listParams';

import OverzichtActionMenu from './OverzichtActionMenu';
import FilterPortfolios from './FilterPortfolios';

import { PortfolioService, portfolioService } from '../../services/portfolio.service';
import NoActiveOrganisatieMessage from '../controls/NoActiveOrganisatieMessage';
import { MijnOrganisatieListItem } from '../../models/user/mijnOrganisatieListItem';
import { userManagementService } from '../../services/user-management.service';
import { LOCAL_STORAGE_KEYS } from '../../services/utils/localStorageKeys';
import { loadJsonFromLocalStorage, saveJsonToLocalStorage } from '../../services/utils/localStorageState';
import { messages } from '../../content';


interface OverzichtPortfoliosProps {
  planId?: string;
  refreshKey?: number;
}

interface StoredAfstemmingsdossierFilterState {
  selectedSearch: string;
  statusFilter: string[];
}

const getStoredAfstemmingsdossierFilterState = (): StoredAfstemmingsdossierFilterState => {
  const fallback: StoredAfstemmingsdossierFilterState = {
    selectedSearch: '',
    statusFilter: [],
  };

  return loadJsonFromLocalStorage<StoredAfstemmingsdossierFilterState>(
    LOCAL_STORAGE_KEYS.AFSTEMMINGSDOSSIERS_FILTERS,
    fallback,
    (parsed) => ({
      selectedSearch: typeof parsed?.selectedSearch === 'string' ? parsed.selectedSearch : '',
      statusFilter: Array.isArray(parsed?.statusFilter) ? parsed.statusFilter : [],
    }),
  );
};

const OverzichtPortfolios: React.FC<OverzichtPortfoliosProps> = ({ 
  planId,
  refreshKey
}) => {
  const navigate = useNavigate();
  const initialFilterStateRef = useRef<StoredAfstemmingsdossierFilterState>(getStoredAfstemmingsdossierFilterState());
  const [mijnOrganisatie] = useState<MijnOrganisatieListItem | null>(() =>
    userManagementService.getStoredOrganisatie()
  );
  const [portfolios, setPortfolios] = useState<any[]>([]);
  
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>(initialFilterStateRef.current.selectedSearch);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initialFilterStateRef.current.selectedSearch);
  const [statusFilter, setStatusFilter] = useState<string[]>(initialFilterStateRef.current.statusFilter);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const activeFilterCount = statusFilter.length;

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    saveJsonToLocalStorage(LOCAL_STORAGE_KEYS.AFSTEMMINGSDOSSIERS_FILTERS, {
      selectedSearch: searchInput,
      statusFilter,
    });
  }, [searchInput, statusFilter]);

  const fetchPortfolios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: ListParams = {
        _offset: pageIndex * pageSize,
        _limit: pageSize,
        _sort: sortBy && sortDirection ? `${sortBy} ${sortDirection}` : undefined,
      };

      if (!planId) {
        params._search = debouncedSearch || undefined;
        if (statusFilter.length === 1) {
          params._filter = `AfstemmingsStatus = '${statusFilter[0]}'`;
        } else if (statusFilter.length > 1) {
          params._filter = `AfstemmingsStatus IN (${statusFilter.map(s => `'${s}'`).join(', ')})`;
        }
      }

      let data = [];
      if (planId) {
        data = await portfolioService.getPortfolioOverviewByPlan(planId, params);
      }
      else {
        data = await portfolioService.getPortfolios(params);
      }

      setPortfolios(data.items);
      setTotalCount(data.count);
    }
    catch (error: any) {
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }, [navigate, planId, pageIndex, pageSize, sortBy, sortDirection, debouncedSearch, statusFilter]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setPageIndex(0);
      setDebouncedSearch(value);
    }, 450);
  };

  const handleStatusChange = (values: string[]) => {
    setStatusFilter(values);
    setPageIndex(0);
  };

  useEffect(() => {
    setReady(true);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    fetchPortfolios();
  }, [ready, fetchPortfolios, refreshKey]);

  const gridColumns = useMemo<(GridColDef<any> | GridActionsColDef<any>)[]>(() => {
    const baseColumns: (GridColDef<any> | GridActionsColDef<any>)[] = [
      {
        field: 'name',
        headerName: 'Naam dossier',
        flex: 3,
        renderCell: (params: any) => {
          const hasAccess = params.row?.hasAccess !== false;

          if (!hasAccess) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, paddingTop: 6, paddingBottom: 6 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>{params.row.name}</span>
                  <Tooltip title={messages.NO_ACCESS_TO_PORTFOLIO} arrow>
                    <Icon fontSize="small" sx={{ color: 'text.secondary' }}>block</Icon>
                  </Tooltip>
                </span>
              </div>
            );
          }

          return (
            <Link className="lnk" to={`/dossiers/${params.row.id}`}>
              {params.row.name}
            </Link>
          );
        },
      }
    ];

    const afstemmingsStatusColumn: GridColDef<any> = {
      field: 'afstemmingsStatus',
      headerName: 'Afstemming',
      flex: 1.5,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const statusOptions = PortfolioService.STATUS_OPTIONS;
        const status = params.row.afstemmingsStatus;
        const statusOption = statusOptions.find(option => option.id === status);
        const icon = statusOption ? statusOption.icon : '';
        const tooltipText = statusOption ? `Afstemmingsstatus: ${statusOption.name} - ${statusOption.tooltip}` : 'Geen status beschikbaar';

        return (
          <Tooltip title={tooltipText} placement="right" arrow>
            <span className={`afstemmings-status-icon ${status}`}>
              <span className="material-icons material-symbols-rounded">{icon}</span>
            </span>
          </Tooltip>
        );
      }
    };

    baseColumns.push(afstemmingsStatusColumn);

    if (planId) {
      baseColumns.push({
        field: "approvalPending",
        headerName: "Deelname voor dit plan geaccepteerd?",
        renderHeader: () => (
          <Tooltip
            title="Geeft aan of deelname voor dit plan geaccepteerd is in dit afstemmingsdossier"
            arrow
          >
            <span>Deelname voor dit plan geaccepteerd?</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => {
          const hasAccess = params.row?.hasAccess !== false;

          if (!hasAccess) {
            return null;
          }

          return <span>{params.row.approvalPending ? "Nee" : "Ja"}</span>;
        }
      });
    }

    if (!planId) {
      baseColumns.push({
        field: "approvalPending",
        headerName: "Deelname volledig geaccepteerd?",
        renderHeader: () => (
          <Tooltip
            title="Geeft aan of alle plannen binnen uw organisatie geaccepteerd of afgewezen zijn in dit afstemmingsdossier"
            arrow
          >
            <span>Deelname volledig geaccepteerd?</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => {
          const isPending = params.row.approvalPending;
          const label = isPending ? "Nee" : "Ja";
          const tooltipText = isPending
            ? "Er is nog niet voor alle plannen van uw organisatie een keuze gemaakt met betrekking tot de deelname in het afstemmingsdossier"
            : "De deelname is voor al uw plannen in het afstemmingsdossier verwerkt";

          return (
            <Tooltip title={tooltipText} arrow>
              <span>{label}</span>
            </Tooltip>
          );
        }
      });

      baseColumns.push({ 
        field: 'inclusionRequestCount', 
        headerName: 'Aantal plannen in afwachting',
        renderHeader: () => (
          <Tooltip
            title="Het totaal aantal plannen in het afstemmingsdossier die nog wachten op goedkeuring om deel te nemen"
            arrow
          >
            <span>Aantal plannen in afwachting</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false
      });
      baseColumns.push({ 
        field: 'planCount', 
        headerName: 'Aantal plannen actief',
        renderHeader: () => (
          <Tooltip
            title="Het totaal aantal plannen die actief deelnemen in het afstemmingsdossier"
            arrow
          >
            <span>Aantal plannen actief</span>
          </Tooltip>
        ), 
        flex: 2,
        sortable: false,
        filterable: false 
      });

      baseColumns.push({
        field: 'consortiums',
        headerName: 'Samenwerkingsverbanden',
        renderHeader: () => (
          <Tooltip
            title="Aantal gekoppelde samenwerkingsverbanden binnen dit afstemmingsdossier"
            arrow
          >
            <span>Samenwerkingsverbanden</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => {
          const consortiums = Array.isArray(params.row.consortiums) ? params.row.consortiums : [];
          const consortiumNames = consortiums
            .map((item: any) => item?.name)
            .filter(Boolean)
            .sort((a: string, b: string) => a.localeCompare(b, 'nl-NL'))
            .join(', ');
          const tooltipText = consortiumNames ? `Samenwerkingsverbanden: ${consortiumNames}` : 'Geen samenwerkingsverbanden gekoppeld';

          return (
            <Tooltip title={tooltipText} arrow>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Badge
                  badgeContent={consortiums.length}
                  color={consortiums.length === 0 ? 'secondary' : 'primary'}
                  showZero
                  sx={{ mr: 1 }}
                >
                  <Icon fontSize="small">groups</Icon>
                </Badge>
              </span>
            </Tooltip>
          );
        }
      });
    }

    // baseColumns.push({
    //   field: 'isActiveDossier',
    //   headerName: 'Dossier is actief',
    //   flex: 2,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params: GridRenderCellParams<any>) => (
    //     <span>{params.row.planCount > 1 ? 'Ja' : 'Nee'}</span>
    //   )
    // });

    baseColumns.push({
      field: 'actions',
      headerName: 'Acties',
      renderHeader: () => <></>,
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const hasAccess = params.row?.hasAccess !== false;

        if (!hasAccess) {
          return null;
        }

        return <OverzichtActionMenu params={params} onPortfolioUpdate={fetchPortfolios} />;
      }
    });
    
    return baseColumns;
  }, [planId, fetchPortfolios]);

  const onChangePagination = ({ page, pageSize }: { page: number, pageSize: number }) => {
    setPageIndex(page);
    setPageSize(pageSize);
  };

  const handleSortChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      setSortBy(field);
      setSortDirection(sort || "asc");
    }
    else {
      setSortBy("");
      setSortDirection("");
    }
  };

  if (!mijnOrganisatie) {
    return (
      <>
        <Paper>
          <NoActiveOrganisatieMessage />
        </Paper>
      </>
    )
  }

  const dataGrid = (
    <div className="grid-responsive">
      <Paper sx={{ width: '100%' }}>
        <DataGrid
          rows={portfolios}
          columns={gridColumns}
          getRowId={(row) => row.id || ''}
          localeText={{
            noRowsLabel: messages.NO_PORTFOLIOS,
            MuiTablePagination: {
              labelRowsPerPage: 'Dossiers per pagina:',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) => `${from}-${to} van ${count}`,
            },
          }}
          disableColumnFilter
          disableRowSelectionOnClick
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                page: pageIndex,
                pageSize: pageSize
              }
            }
          }}
          onPaginationModelChange={onChangePagination}
          onSortModelChange={handleSortChange}
          sortingMode="server"
          sx={{ border: 0 }}
        />
      </Paper>
    </div>
  );

  if (planId) {
    return (
      <>
        {dataGrid}
        {loading && <ProgressBar />}
        {error && <ErrorMessage message={error} />}
      </>
    );
  }

  return (
    <div className="portfolio-with-filter-panel">
      <div className={`filter-panel-left${filterPanelOpen ? '' : ' filter-panel-left--closed'}`}>
        <FilterPortfolios
          statusFilter={statusFilter}
          onChange={handleStatusChange}
          onClose={() => setFilterPanelOpen(false)}
        />
      </div>

      <div className="portfolio-content-area">
        <div className="sidebar-search-bar">
          <TextField
            label="Zoeken in afstemmingsdossiers"
            variant="filled"
            placeholder="Zoeken op naam of omschrijving"
            value={searchInput}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (debounceTimer.current) clearTimeout(debounceTimer.current);
                      setSearchInput('');
                      setDebouncedSearch('');
                      setPageIndex(0);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <Icon fontSize="small">clear</Icon>
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Tooltip
            title={activeFilterCount > 0 ? `${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} actief` : ''}
            disableHoverListener={activeFilterCount === 0}
          >
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              overlap="rectangular"
              sx={{ '& .MuiBadge-badge': { top: 6, right: 6 } }}
            >
              <Tooltip title={filterPanelOpen ? 'Sluit het filter' : 'Open filter om uitgebreid te zoeken in afstemmingsdossiers'}>
                <IconButton size="large" onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
                  <Icon>{filterPanelOpen ? 'filter_list_off' : 'filter_list'}</Icon>
                </IconButton>
              </Tooltip>
            </Badge>
          </Tooltip>
        </div>

        {dataGrid}

        {loading && <ProgressBar />}
        {error && <ErrorMessage message={error} />}
      </div>
    </div>
  );
};

export default OverzichtPortfolios;
